<template>
  <div data-cy="membership-health-dashboard">
    <div
      v-if="hasNoPrograms"
      class="text-center text-gray-500 py-8"
      data-cy="membership-health-dashboard-empty-state"
    >
      No membership programs configured for this tenant.
    </div>

    <div v-else class="dashboard-body">
      <header class="dashboard-body__header">
        <div class="dashboard-body__heading">
          <h1 class="dashboard-body__title">Membership Health</h1>
          <p class="dashboard-body__subtitle" data-cy="dashboard-subtitle">
            {{ subtitleText }}
          </p>
        </div>
        <MembershipHealthDashboardFilters />
      </header>

      <MembershipHealthHero />

      <div class="dashboard-body__triple">
        <MembershipAtRiskCard />
        <MembershipActionsCard />
        <MetricTile
          label="Recurring Revenue"
          :value="mrrValue"
          :tooltip="REVENUE_TOOLTIP"
          :helper-text="arrCaption"
          :href="recurringRevenueHref"
          :loading="loading"
          data-cy="metric-recurring-revenue"
        />
      </div>

      <div class="dashboard-body__trend" data-cy="trend-section">
        <MembershipGrowthChart />
        <MembershipRevenueComparison />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted } from "vue";
import { useStore } from "vuex";

import MembershipActionsCard from "./MembershipActionsCard.vue";
import MembershipAtRiskCard from "./MembershipAtRiskCard.vue";
import MembershipGrowthChart from "./MembershipGrowthChart.vue";
import MembershipHealthDashboardFilters from "./MembershipHealthDashboardFilters.vue";
import MembershipHealthHero from "./MembershipHealthHero.vue";
import MembershipRevenueComparison from "./MembershipRevenueComparison.vue";
import MetricTile from "./MetricTile.vue";
import { useMembershipDashboard } from "./useMembershipDashboard";

const REVENUE_TOOLTIP =
  "Monthly value of auto-renewing memberships, normalized to a per-month rate. Non-renewing memberships are excluded. Annualized = monthly × 12.";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD"
});

const store = useStore();
const { summary, loading, selectedProgramIds, programs, membersReportHref } =
  useMembershipDashboard();

// Read URL filters and commit synchronously so child components see the
// populated filter state during their own setup. Without this, children
// mount with an empty filter, fetch once, and then re-fetch when the
// parent's onMounted commits the URL filters.
const initialProgramIds = new URLSearchParams(window.location.search)
  .getAll("membership_program_ids[]")
  .map(Number)
  .filter(Number.isFinite);
if (initialProgramIds.length) {
  store.commit("replace", {
    state: "membershipDashboardFilters",
    data: { membership_program_ids: initialProgramIds }
  });
}

const hasNoPrograms = computed<boolean>(
  () => store.state.membershipPrograms.length === 0
);

const subtitleText = computed<string>(() => {
  const selected = selectedProgramIds.value;
  const total = programs.value.length;
  if (selected.length === 0) {
    return total === 1 ? "Showing 1 program" : `Showing all ${total} programs`;
  }
  if (selected.length === 1) {
    const program = programs.value.find((p) => p.id === selected[0]);
    return program ? `Showing ${program.name}` : "Showing 1 program";
  }
  return `Showing ${selected.length} of ${total} programs`;
});

const mrrValue = computed<string | null>(() => {
  const cents = summary.value?.monthly_recurring_revenue_cents;
  if (cents === null || cents === undefined) return null;
  return `${currencyFormatter.format(cents / 100)}/mo`;
});

const arrCaption = computed<string>(() => {
  const cents = summary.value?.annual_recurring_revenue_cents;
  if (cents === null || cents === undefined) return "";
  return `${currencyFormatter.format(cents / 100)} annualized`;
});

const recurringRevenueHref = computed(() =>
  membersReportHref("/reports/members?membership_program_status=current")
);

onMounted(async () => {
  await store.dispatch("fetchMembershipPrograms", true);
  store.dispatch("fetchMembershipDashboardSummary");
});

onBeforeUnmount(() => {
  store.dispatch("clearMembershipDashboardData");
});
</script>

<style lang="scss" scoped>
.dashboard-body {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-bottom: 1.5rem;
}
.dashboard-body__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.dashboard-body__heading {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}
.dashboard-body__title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  letter-spacing: -0.01em;
  line-height: 1.2;
}
.dashboard-body__subtitle {
  margin: 0;
  font-size: 0.875rem;
  color: #6b7280;
}
.dashboard-body__triple {
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;
}
@media (min-width: 640px) {
  .dashboard-body__triple {
    grid-template-columns: 1fr 1fr;
  }
}
@media (min-width: 1024px) {
  .dashboard-body__triple {
    grid-template-columns: 1fr 1fr 1fr;
  }
}
.dashboard-body__trend {
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;
}
@media (min-width: 640px) {
  .dashboard-body__trend {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (min-width: 1024px) {
  .dashboard-body__trend {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .dashboard-body__trend > :first-child {
    grid-column: span 2;
  }
}
</style>
